import { CommonModule, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    forwardRef,
    inject
} from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { App } from '@df/core/app';
import { ElementHeightDirective } from '@df/dom/element-height/element-height.directive';
import { AbIfDirective } from '@df/module-ab/ab-if.directive';
import { AbIfService } from '@df/module-ab/ab-if.service';
import { AbModule } from '@df/module-ab/ab.module';
import { AbService } from '@df/module-ab/ab.service';
import { Ga4TrackClickDirective } from '@df/module-gtm/ga4-track-click.directive';
import { ImageModule } from '@df/module-image/image.module';
import { IfLoggedInDirective } from '@df/session/customer/if-logged-in.directive';
import { IfNotLoggedInDirective } from '@df/session/customer/if-not-logged-in.directive';
import { AfterContentInitDirective, AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { ClickEventDirective } from '@df/ui/common/click-event.directive';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { ToggleService } from '@df/ui/toggle/toggle.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent } from '@icms/core/content';
import { BehaviorSubject, Subscription } from 'rxjs';
import { NCmsMinibasket } from '../../../basket/minibasket/minibasket.component';
import { BasketQtyBulletDirective } from '../../../basket/qty/basket-qty-bullet.directive';
import { PageComponentNotificationBarComponent } from '../../../catalog/page/component/notification-bar/notification-bar.component';
import { StickyFreeComponent } from '../../../common/sticky-free/sticky-free.component';
import { OpenSearchWrapperComponent } from '../../../search/ui/open-search-wrapper.component';
import { SearchModalComponent } from '../../../search/ui/search-modal.component';
import { SearchOutletComponent } from '../../../search/ui/search-outlet.component';
import { IfCheckoutProcessDirective } from '../../../ui/common/if-checkout-process.directive';
import { IfNotCheckoutProcessDirective } from '../../../ui/common/if-not-checkout-process.directive';
import { TagComponent } from '../../../ui/display/tag/tag.component';
import { WishlistQtyComponent } from '../../../wishlist/qty/wishlist-qty.component';
import { IMenuTab, addMenuTabs } from '../../add-menu-tabs';
import { MeganavContentComponent } from '../../meganav/meganav-content.component';
import { MeganavOverlayComponent } from '../../meganav/meganav-overlay.component';
import { MeganavComponent } from '../../meganav/meganav.component';
import { PAGE_MENU_TAB_PREFERENCE_FIELD } from './../../../catalog/page/page.config';
import { PageHeaderBodyDirective } from './page-header-body.directive';
import { PAGE_HEADER_BLOCK_CONFIG } from './page-header.config';
import { NPageHeader } from './page-header.interface';
import { PageHeaderService } from './page-header.service';

addMenuTabs(PAGE_HEADER_BLOCK_CONFIG);
export const HEADER_ID = 'header';
@Component({
    selector: 'page-header',
    templateUrl: 'page-header.html',
    styleUrls: ['./page-header.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        IfCheckoutProcessDirective,
        IfNotCheckoutProcessDirective,
        IfSizeDirective,
        NgTemplateOutlet,
        ICmsClientModule,
        MeganavComponent,
        StickyFreeComponent,
        forwardRef(() => PageComponentNotificationBarComponent),
        ImageModule,
        BasketQtyBulletDirective,
        IfLoggedInDirective,
        IfNotLoggedInDirective,
        WishlistQtyComponent,
        RouterLink,
        TranslationModule,
        Ga4TrackClickDirective,
        MeganavOverlayComponent,
        SearchOutletComponent,
        ToggleModule,
        MeganavContentComponent,
        TagComponent,
        SearchModalComponent,
        ElementHeightDirective,
        AbModule,
        CommonModule,
        AfterContentInitDirective,
        ClickEventDirective,
        PageHeaderBodyDirective,
        OpenSearchWrapperComponent,
        AbIfDirective
    ]
})
@ICmsComponent<PageHeaderComponent>(PAGE_HEADER_BLOCK_CONFIG)
export class PageHeaderComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit, OnDestroy {
    @ICmsContent({
        type: 'blockContent'
    })
    content!: NPageHeader.IBlockContent;

    hasContent$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    @Input()
    offCanvasState!: BehaviorSubject<boolean>;
    readonly meganavToggleGroupId = NPageHeader.MEGANAV_GROUP_ID;

    zippyCloseEventName = 'meganav-content.zippy.close';
    headerId = HEADER_ID;
    minibasketModalId = NCmsMinibasket.BLOCK_ID;
    private _activeMenuTab?: IMenuTab<NPageHeader.IMenuItem>;
    imageTag: 'h1' | 'p' = 'h1';
    private afterContentInitSubscription?: Subscription;

    protected router = inject(Router);
    app = inject(App);
    protected pageHeaderService = inject(PageHeaderService);
    abIfService = inject(AbIfService);
    afterContentInitDirectiveService = inject(AfterContentInitDirectiveService);

    abService = inject(AbService);

    nodes$ = new BehaviorSubject<HTMLElement[]>([]);

    private elementRef = inject(ElementRef);

    private toggleService = inject(ToggleService);

    override ngOnInit(): void {
        super.ngOnInit();

        this.afterContentInitSubscription = this.afterContentInitDirectiveService.loaded.subscribe(value => {
            if (value) {
                this.detectChanges();
                // we cant unsubscribe as you may have element hidden on some pages (checkout) and then restored
                // this.afterContentInitSubscription?.unsubscribe();
                // this.afterContentInitSubscription = undefined;
            }
        });
        this.subscriptions.add(this.app.meta.subscribe(() => this.onMetaModelUpdate()));
        this.onMetaModelUpdate();
        this.subscriptions.add(
            this.router.events.subscribe(event => {
                if (event instanceof NavigationEnd) {
                    this.imageTag = event.url === '/' ? 'h1' : 'p';
                    this.detectChanges();
                }
            })
        );
        if (this.app.isPlatformBrowser) {
            this.imageTag = location.pathname === '/' ? 'h1' : 'p';
        }

        this.detectChanges();
        this.nodes$.next([this.elementRef.nativeElement]);
        this.subscriptions.add(this.abService.getSubject('headerOpenSearch').subscribe(() => this.detectChanges()));
    }

    private onMetaModelUpdate(): void {
        const model = this.app.meta.model;
        if (model?.data && model.type === 'page') {
            if (model.data['additional']) {
                const menuTabPreferenceId = model.data['additional'][PAGE_MENU_TAB_PREFERENCE_FIELD.key];
                if (menuTabPreferenceId) {
                    const preferenceTabItem = this.menuTabs.find(i => i.id === menuTabPreferenceId);
                    if (preferenceTabItem) {
                        this.activeMenuTab = preferenceTabItem;
                    }
                }
            }
        }
    }
    protected onModelChange(): void {
        this.updateClassNames();
        this.onMetaModelUpdate();
    }
    get isCms(): boolean {
        return this.client.isCms && !this.client.isCmsPreview;
    }
    get menuTabs(): IMenuTab<NPageHeader.IMenuItem>[] {
        return this.content?.menuTabs || [];
    }

    get activeMenuTab(): IMenuTab<NPageHeader.IMenuItem> {
        return (
            this._activeMenuTab ||
            this.menuTabs.find(tab => !tab.abIfExp || this.abIfService.getState(tab.abIfExp).value) ||
            this.menuTabs[0]
        );
    }
    set activeMenuTab(tab: IMenuTab<NPageHeader.IMenuItem>) {
        this._activeMenuTab = tab;
        this.app.events.broadcast(this.zippyCloseEventName);
        this.detectChanges();
    }
    isActiveTab(tab: IMenuTab<NPageHeader.IMenuItem>): boolean {
        return this.activeMenuTab === tab;
    }
    get menuItems(): NPageHeader.IMenuItem[] {
        return this.activeMenuTab?.menu || [];
    }
    get logoURL(): string {
        return this.client.isS || this.client.isM ? '/basket' : '/';
    }

    protected updateClassNames(): void {
        this.pageHeaderService.setLowPriorityState({
            transparent: this.transparent,
            lightMode: this.lightMode,
            hideUp: this.hideUp
        });
    }

    protected get transparent(): boolean | undefined {
        return this.content?.additional?.headerTransparent;
    }

    protected get lightMode(): boolean | undefined {
        return this.content?.additional?.headerLightMode;
    }

    protected get hideUp(): boolean | undefined {
        return this.content?.additional?.headerHideUp;
    }

    openOffCanvas() {
        this.offCanvasState.next(true);
    }

    override detectChanges() {
        super.detectChanges();
        const hasContent = !!this.content;
        if (this.hasContent$.value !== hasContent) {
            this.hasContent$.next(!!hasContent);
        }
    }

    toggleCmsNode(element: HTMLElement, state: boolean) {
        const nodes = this.nodes$.value;
        if (state) {
            if (!nodes.includes(element)) {
                this.nodes$.next([...nodes, element]);
            }
        } else {
            if (nodes.includes(element)) {
                this.nodes$.next(nodes.filter(node => node !== element));
            }
        }
    }

    get isOpenSearch() {
        return this.abService.getVariant('headerOpenSearch') === 'B';
    }
}
